import React, { useEffect } from "react";
import PropTypes from "prop-types";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import AppUtils from "@src/utils/appUtils";
import SignolUtils from "@src/utils/signolUtils";
import StorageUtils from "@src/utils/storageUtils";
import { LOGGING_OUT } from "@src/constants/constants";
import { trackActionActivity } from "@src/utils/userActivityTracker";
import RootAppLayout from "@src/components/layout/RootAppLayout";
import Button from "@src/components/atoms/Button";

// http://localhost:3000/feedbackFromEmails?email=test%40example.com&operatorCode=017873&signupLink=/signup?token=abc123&feedback=useful
const FeedbackFromEmailsPage = () => {
  const [searchParams] = useSearchParams();
  const email = searchParams.get("email");
  const operatorCode = searchParams.get("operatorCode");
  const signupLink = searchParams.get("signupLink");
  const feedback = searchParams.get("feedback");
  const location = useLocation();
  const currentOperatorCode = SignolUtils.getCurrentOperatorCode();
  const isCurrentUser = currentOperatorCode === operatorCode;

  // If there's a signup link or the operator code doesn't match the currently logged-in user,
  // log the user out (if any) and redirect to the signup link or login page.
  useEffect(() => {
    try {
      if (signupLink || !isCurrentUser) {
        console.log("Performing conditional logout", {
          signupLink,
          isCurrentUser,
        });
        SignolUtils.removeSessionCookies();
        StorageUtils.setItem(LOGGING_OUT, Date.now().toString());
      }
    } catch (e) {
      console.warn("Failed to perform conditional logout/redirect", e);
    }
  }, [signupLink, isCurrentUser]);

  // Track a page view-like activity including all URL params
  useEffect(() => {
    try {
      const params = Object.fromEntries(searchParams.entries());
      if (params.feedback === "useful" || params.feedback === "not_really") {
        trackActionActivity(
          "FEEDBACK_FROM_EMAIL_PAGE_VIEW",
          { path: location.pathname + location.search, params },
          true,
        );
      }
    } catch (e) {
      console.warn("Failed to track feedback page view", e);
    }
  }, [location, searchParams]);

  return (
    <RootAppLayout>
      <div className="bg-white rounded-2xl mx-8 md:mx-0 overflow-clip w-full max-w-md xl:max-w-lg">
        <div className="px-8 py-8 text-center">
          <img
            src="https://signol-email-images.s3.eu-west-2.amazonaws.com/handshake.png"
            alt="Feedback Illustration"
            className="w-full h-24 object-contain mb-6"
          />
          <div>
            <h2 className="text-2xl text-primary-500 font-extrabold mb-4">
              Thank you
            </h2>
          </div>
          <div>
            <p>Your feedback helps us improve Signol for everyone.</p>
          </div>
          <div className="w-full flex justify-center">
            <Button
              className="mx-auto mb-4"
              block={false}
              onClick={() => {
                if (signupLink) {
                  // remove any leading "http(s)://domain.com" from the signupLink to avoid open redirect issues, but allow relative paths
                  const path = signupLink.startsWith("/")
                    ? signupLink
                    : signupLink.split("/").slice(-1)[0];
                  const safeSignupLink = path;
                  window.location.href = safeSignupLink;
                } else {
                  window.location.href = "/login";
                }
              }}
            >
              {signupLink ? "Access my account" : "Log in to my account"}
            </Button>
          </div>
        </div>
      </div>
    </RootAppLayout>
  );
};

export const feedbackFromEmailsControls = {};

FeedbackFromEmailsPage.propTypes = AppUtils.getPropTypeFromControls(
  feedbackFromEmailsControls,
);

export default FeedbackFromEmailsPage;
